/**
 * Marks a CLIENT-rendered page as such, so the browser bootstrap does not log NG0505.
 *
 * `provideClientHydration()` lives in the shared `appConfig`. It must, because the
 * server needs it to serialize hydration data. So the browser always asks for
 * hydration, and Angular then looks for that data in the `<script id="ng-state">`
 * TransferState blob. When the data is missing, Angular skips hydration and renders
 * from scratch, which is correct. But it also warns NG0505 ("hydration was requested
 * on the client, but there was no serialized information…") unless `<body>` carries
 * the `ngcm` (client render mode) flag.
 *
 * `@angular/build` adds that flag ONLY to `index.csr.html`, the shell that an SSR
 * build serves for `RenderMode.Client` routes. Our other client-rendered shell never
 * gets it: the plain `index.html` that `ng serve` sends under the `serve-csr`
 * configuration (`server: false`, see angular.json). So every `npm start` page load
 * logged NG0505 although nothing was wrong.
 *
 * This sets the flag the same way the build does, and only when the server sent no
 * hydration data. When it did, the page is left alone and hydrates exactly as before.
 * In both cases hydration behaves the same as without this guard: Angular uses the
 * same TransferState check to choose between hydrating and rendering from scratch,
 * and `ngcm` is read only to decide whether to log the warning.
 *
 * Call it in `main.ts` before `bootstrapApplication`.
 */

/** Angular's `CLIENT_RENDER_MODE_FLAG` (`ɵCLIENT_RENDER_MODE_FLAG` in `@angular/core`). */
const CLIENT_RENDER_MODE_FLAG = 'ngcm';

/** TransferState script id for the default `APP_ID` (`'ng'` → `ng-state`). */
const TRANSFER_STATE_SCRIPT_ID = 'ng-state';

/** Angular's `NGH_DATA_KEY` — the TransferState key holding the hydration data. */
const HYDRATION_DATA_KEY = '__nghData__';

/** True when the server serialized hydration data, i.e. Angular will hydrate. */
export function hasServerHydrationData(doc: Document): boolean {
  const script = doc.getElementById(TRANSFER_STATE_SCRIPT_ID);
  if (script?.tagName !== 'SCRIPT' || !script.textContent) return false;
  try {
    const state = JSON.parse(script.textContent) as Record<string, unknown> | null;
    return state?.[HYDRATION_DATA_KEY] != null;
  } catch {
    return false;
  }
}

/** Flags `<body>` as client-rendered when there is no server hydration data. */
export function markClientRenderModeIfNotServerRendered(doc: Document = document): void {
  if (!doc.body || hasServerHydrationData(doc)) return;
  doc.body.setAttribute(CLIENT_RENDER_MODE_FLAG, '');
}
